/**
 * This is the main implementation of the navigation component, only
 * missing the bits that require MooTools for implementation (those
 * are implemented in `./navigation` and passed into this component as
 * props.
 */
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useEffect, useRef } from 'react';

export function Navigation({ links, heading, pageTitle, onIfixit, isLoggedIn, tabsOnly }: any) {
   const visibleTabs = links.tabLinks.filter((tab: any) => tab.showTab);
   const headingText = heading ?? pageTitle;
   const currentTabRef = useRef<HTMLAnchorElement | null>(null);
   const stripRef = useRef<HTMLDivElement | null>(null);

   function getActiveTabIndex() {
      let index = 0;
      while (index < visibleTabs.length) {
         const tab = visibleTabs[index];
         if (tab.label === pageTitle) {
            break;
         }
         index++;
      }
      return index;
   }

   const activeTabIndex = getActiveTabIndex();
   const currentTabData = visibleTabs[activeTabIndex];
   const activeValue = visibleTabs[activeTabIndex]?.label ?? '';

   // Five tabs still outrun a phone, so the last ones start off the right
   // edge behind the fade. Arriving on one of those from a link showed a
   // strip with no lit tab in it at all.
   //
   // Driving scrollLeft rather than calling scrollIntoView: that scrolls
   // every scrollable ancestor, and `nearest` only means "the smallest
   // scroll", not "leave this axis alone". Landing part-way down one of
   // these pages, hydration would have yanked the document back up to
   // bring the strip into view. This touches one axis of one element.
   useEffect(() => {
      const tab = currentTabRef.current;
      const strip = stripRef.current;
      if (!tab || !strip) {
         return;
      }
      const tabBox = tab.getBoundingClientRect();
      const stripBox = strip.getBoundingClientRect();
      // Matches scroll-fade-x's gutter, so the tab lands past the ramp
      // rather than under the transparent end of it.
      const gutter = 16;
      if (tabBox.left < stripBox.left + gutter) {
         strip.scrollLeft -= stripBox.left + gutter - tabBox.left;
      } else if (tabBox.right > stripBox.right - gutter) {
         strip.scrollLeft += tabBox.right - stripBox.right + gutter;
      }
   }, []);

   return (
      <div id="communityNav" className={cn(!tabsOnly && 'mt-6')}>
         <nav aria-label={_js('Community')}>
            {/* The store's secondary navbar: white, 48px, one hairline under
                it, running the full width of the window while the tabs keep to
                the page's own 1024px column, where the H1 under them starts.
                The band is painted by `::before`, not by the wrapper: 100vw
                counts the scrollbar, so a `w-screen` wrapper hands its column
                a box one scrollbar wider than the page root's, and below the
                1024px cap the first tab lands half a scrollbar left of the H1.
                `border-border` is spelled out because the base `*` rule that
                colors every border does not reach pseudo-elements. The
                sideways scroll 100vw leaves behind is answered by
                `clipHorizontal` in `v2_editor_chrome.phtml`, which holds from
                the first paint.

                The two v1 pages this still renders for (the nanosite index,
                the old translate dashboard) lay out full width, with no column
                for a band to line up with. There the row stays plain and keeps
                the gap it always had above it. */}
            <div
               className={cn(
                  tabsOnly &&
                     'relative before:absolute before:inset-y-0 before:left-[calc(50%-50vw)] before:w-screen before:border-b before:border-border before:bg-white'
               )}
            >
               <div className={cn(tabsOnly && 'relative mx-auto w-full max-w-5xl px-4 md:px-8')}>
                  <div
                     ref={stripRef}
                     className={cn(
                        'scroll-fade-x flex h-12 items-stretch',
                        // No band to draw one, so the row carries its own
                        // hairline. The tabs come down a pixel onto it, so the
                        // lit one's underline covers the hairline instead of
                        // stacking 2px of brand on top of it.
                        !tabsOnly && 'border-b [&>a]:-mb-px'
                     )}
                  >
                     {visibleTabs.map((tab: any) => (
                        <a
                           key={tab.label}
                           ref={tab.label === activeValue ? currentTabRef : undefined}
                           href={tab.url}
                           aria-current={tab.label === activeValue ? 'page' : undefined}
                           // No padding on the first tab: its label starts on
                           // the same line as the H1 under it, which is where
                           // the store bar starts its breadcrumbs.
                           className="inline-flex items-center whitespace-nowrap border-transparent border-b-2 px-4 text-gray-600 text-sm no-underline transition-colors first:pl-0 hover:text-gray-900 hover:no-underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset aria-[current=page]:border-brand-500 aria-[current=page]:font-medium aria-[current=page]:text-brand-500"
                        >
                           {tab.label}
                        </a>
                     ))}
                  </div>
               </div>
            </div>
         </nav>
         {/* tabsOnly: the page's own React body renders the H1 and help
             link, so the strip is all this component contributes. */}
         {tabsOnly ? null : (
            <header className="mt-6 flex flex-wrap items-center justify-center md:justify-between">
               <h1 className="mb-1.5 w-full text-[30px] font-medium text-gray-800 md:mb-0 md:w-auto">
                  {headingText}
               </h1>
               <div className="hidden flex-auto flex-wrap justify-end gap-2 md:flex md:gap-4">
                  {onIfixit && (
                     <a
                        href={currentTabData?.help}
                        className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                           trackAnalyticsCustomEvent({
                              eventCategory: pageTitle,
                              eventAction: `${pageTitle} - Btn "How this Works"`,
                              eventName: currentTabData?.help,
                           });
                        }}
                     >
                        {_js('How this Works')}
                        <FaIcon name="circle-info" className="size-4" />
                     </a>
                  )}
                  {!isLoggedIn && (
                     <a
                        href="/Join"
                        className="buttonLink inline-block"
                        onClick={() => {
                           trackAnalyticsCustomEvent({
                              eventCategory: pageTitle,
                              eventAction: `${pageTitle} - Btn - Join`,
                           });
                        }}
                     >
                        {_js('Join')}
                     </a>
                  )}
               </div>
            </header>
         )}
      </div>
   );
}
